import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import { APP_VERSION, BUILD_TIME, GIT_COMMIT, versionLabel, withV } from '@/config/version';
import { reloadOnceForBackendBuild, versionService, type BackendBuild } from '@/services/version.service';

interface VersionBadgeProps {
  /** Text alignment of the badge line (the login page centres it). */
  align?: 'left' | 'center';
  sx?: SxProps<Theme>;
}

// A local backend reports "unknown" / "dev" (scripts/build.sh without ldflags);
// that is not a different build, just an unidentified one.
function knownCommit(commit: string): string {
  return commit && commit !== 'unknown' ? commit : '';
}

function isDifferentBuild(backendCommit: string): boolean {
  const c = knownCommit(backendCommit);
  return Boolean(GIT_COMMIT && c && c !== GIT_COMMIT);
}

function formatBuildTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-TW', { hour12: false });
}

/**
 * The build version of the console, shown on the login page and at the foot
 * of the sidebar. Hovering shows the full frontend build (version, commit,
 * time) and the backend's own build; when the two commits differ the badge
 * says so, because that is the first thing support wants to know.
 */
export default function VersionBadge({ align = 'left', sx }: VersionBadgeProps) {
  const [backend, setBackend] = useState<BackendBuild | null>(null);

  useEffect(() => {
    let cancelled = false;
    versionService.getBackendBuild().then((b) => {
      if (cancelled) return;
      setBackend(b);
      // QA #45: a browser still running the previous build's bundle calls API
      // paths the new backend no longer has. Reload once per backend commit so
      // it picks up the matching bundle (index.html is served no-cache).
      if (b && isDifferentBuild(b.git_commit)) reloadOnceForBackendBuild(b.git_commit);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const backendCommit = backend ? knownCommit(backend.git_commit) : '';
  const mismatch = isDifferentBuild(backendCommit);

  const details = (
    <Box sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      <div>前端 {withV(APP_VERSION)}{GIT_COMMIT ? ` (${GIT_COMMIT})` : ''}</div>
      {BUILD_TIME && <div>建置時間 {formatBuildTime(BUILD_TIME)}</div>}
      {backend ? (
        <div>
          後端 {withV(backend.version.split('+')[0] || 'dev')}
          {backendCommit ? ` (${backendCommit})` : ''}
        </div>
      ) : (
        <div>後端版本：無法取得</div>
      )}
      {mismatch && <div>前後端來自不同的建置</div>}
    </Box>
  );

  return (
    <Tooltip title={details} placement="top" arrow>
      <Typography
        variant="caption"
        component="div"
        data-testid="version-badge"
        sx={{
          color: mismatch ? 'warning.main' : 'text.disabled',
          textAlign: align,
          cursor: 'default',
          userSelect: 'text',
          fontVariantNumeric: 'tabular-nums',
          ...sx,
        }}
      >
        {versionLabel()}
        {mismatch && ' · 後端不同版'}
      </Typography>
    </Tooltip>
  );
}
